import { useEffect, useRef, useState } from "react";
import {
  Activity,
  BadgeCheck,
  Bell,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
  Send,
  ShieldCheck,
  Store,
  Users,
  Warehouse,
} from "lucide-react";

const FragmentRow = ({
  team,
  isOpen,
  members,
  activeDrivers,
  activeDriverCodeSet,
  memberLoading,
  shops,
  allShops,
  openAssignDropdownTeamId,
  assignWorkingCode,
  dutyMap,
  dutyWorkingId,
  onToggleDuty,
  onToggle,
  onOpenAssign,
  onToggleAssignDropdown,
  onAssignShopFromDropdown,
  onOpenSingleNotif,
  onOpenBulkNotif,
}) => {
  const [assignDropdownSearch, setAssignDropdownSearch] = useState("");
  const assignDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openAssignDropdownTeamId === team.id &&
        assignDropdownRef.current &&
        !assignDropdownRef.current.contains(event.target)
      ) {
        onToggleAssignDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openAssignDropdownTeamId, team.id, onToggleAssignDropdown]);

  return (
    <>
      {/* Collapsed Row */}
      <tr className="align-top border-t text-sm transition-colors duration-200 hover:bg-orange-50/40">
        <td className="px-3 py-3 text-center align-middle">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={onToggle}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white transition hover:bg-gray-50"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
          </div>
        </td>

        {/* Team */}
        <td className="px-4 py-3">
          <div className="min-w-[220px] space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                <Users size={18} />
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">
                  {team.team_name || "—"}
                </p>

                <div className="mt-1 flex flex-wrap gap-1.5">
                  <InfoBadgeInline color="blue">ID #{team.id}</InfoBadgeInline>

                  <InfoBadgeInline color="gray">
                    {team.user_type || "—"}
                  </InfoBadgeInline>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CalendarDays size={13} className="shrink-0 text-gray-400" />

              <span>{formatTeamDate(team.createdOn)}</span>
            </div>
          </div>
        </td>

        {/* Coverage / Rules */}
        <td className="px-4 py-3">
          <div className="min-w-[220px] space-y-2 text-xs text-gray-700">
            <div className="flex items-center gap-2">
              <MapPin size={13} className="shrink-0 text-gray-400" />

              <span>
                <span className="font-medium text-gray-600">Accuracy:</span>{" "}
                {team.location_accuracy || "—"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <ShieldCheck size={13} className="shrink-0 text-gray-400" />

              <span>
                <span className="font-medium text-gray-600">Min drivers:</span>{" "}
                {team.minimum_drivers ?? "—"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Warehouse size={13} className="shrink-0 text-gray-400" />

              <span>
                <span className="font-medium text-gray-600">Max drivers:</span>{" "}
                {team.maximum_drivers ?? "—"}
              </span>
            </div>
          </div>
        </td>

        {/* Members / Shops */}
        <td className="px-4 py-3">
          <div className="min-w-[200px] space-y-2">
            <div className="flex flex-wrap gap-1.5">
              <InfoBadgeInline color="blue">
                <span className="inline-flex items-center gap-1">
                  <Users size={11} />
                  {members.length} Members
                </span>
              </InfoBadgeInline>

              <InfoBadgeInline color="green">
                <span className="inline-flex items-center gap-1">
                  <Store size={11} />
                  {shops.length} Shops
                </span>
              </InfoBadgeInline>
            </div>

            <div className="text-xs text-gray-500">
              {members.length > 0
                ? `${members
                    .slice(0, 2)
                    .map(
                      (m) =>
                        m.fullName ||
                        `${m.first_name || ""} ${m.last_name || ""}`.trim(),
                    )
                    .filter(Boolean)
                    .join(", ")}${
                    members.length > 2 ? ` +${members.length - 2} more` : ""
                  }`
                : "No team members yet"}
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="px-4 py-3">
          <div className="min-w-[150px] space-y-2">
            <StatusPillInline status={team.status} />

            <div className="text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <Activity size={12} className="text-gray-400" />
                {activeDrivers.length} active
              </span>
            </div>
          </div>
        </td>

        {/* Actions */}
        <td className="px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="relative">
              <button
                onClick={onToggleAssignDropdown}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700"
              >
                <Building2 size={14} />
                Assign
              </button>

              {openAssignDropdownTeamId === team.id && (
                <div
                  ref={assignDropdownRef}
                  className="absolute right-0 top-9 z-40 w-[420px] rounded-lg border bg-white shadow-lg"
                >
                  <div className="border-b px-3 py-2 text-xs font-semibold text-gray-700">
                    Select Shop
                  </div>

                  <div className="p-3">
                    <div className="relative mb-3">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        autoFocus
                        type="text"
                        value={assignDropdownSearch}
                        onChange={(e) =>
                          setAssignDropdownSearch(e.target.value)
                        }
                        placeholder="Search shop..."
                        className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                      />
                    </div>

                    <div className="max-h-[260px] overflow-y-auto">
                      {(() => {
                        const filteredDropdownShops = (allShops || []).filter(
                          (shop) => {
                            const q = assignDropdownSearch.toLowerCase();

                            return (
                              shop.shop_name?.toLowerCase().includes(q) ||
                              shop.shop_code?.toLowerCase().includes(q) ||
                              shop.shop_address?.toLowerCase().includes(q)
                            );
                          },
                        );

                        return (
                          <>
                            {filteredDropdownShops.map((shop) => (
                              <button
                                key={shop.id}
                                onClick={() =>
                                  onAssignShopFromDropdown(shop.shop_code)
                                }
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b last:border-b-0"
                              >
                                <div className="font-medium">
                                  {shop.shop_name}
                                </div>

                                <div className="text-xs text-gray-500">
                                  {shop.shop_code}
                                </div>

                                <div className="text-xs text-gray-400 truncate">
                                  {shop.shop_address}
                                </div>
                              </button>
                            ))}

                            {filteredDropdownShops.length === 0 && (
                              <div className="p-4 text-center text-sm text-gray-500">
                                No shops found
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={onOpenBulkNotif}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-purple-600 transition hover:bg-purple-50"
              title="Send bulk notification"
            >
              <Bell size={15} />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded Content */}
      {isOpen && (
        <tr>
          <td colSpan={6} className="px-4 py-3">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Members */}
              <section className="border rounded-lg bg-white">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Users size={16} />

                    <h3 className="text-sm font-semibold">Team Members</h3>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={onOpenBulkNotif}
                      className="inline-flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2.5 py-1 rounded"
                      title="Send a notification to all members of this team"
                    >
                      <Send size={14} />
                      Send Bulk
                    </button>
                  </div>
                </div>

                <div className="p-3">
                  {memberLoading ? (
                    <div className="py-8 flex items-center justify-center text-gray-500">
                      <Loader2 size={18} className="animate-spin mr-2" />

                      <span className="text-xs">Loading team members...</span>
                    </div>
                  ) : members.length === 0 ? (
                    <p className="text-xs text-gray-500">No members added.</p>
                  ) : (
                    <ul className="space-y-2">
                      {members.map((m) => {
                        const driverId = m.driver_code || m.id;

                        const fullName =
                          m.fullName ||
                          `${m.first_name || ""} ${m.last_name || ""}`.trim();

                        const duty = dutyMap.get(driverId) || "off_duty";

                        const isOn = duty === "on_duty";

                        const working = dutyWorkingId === driverId;

                        const isActiveInTeam =
                          activeDriverCodeSet.has(driverId);

                        return (
                          <li
                            key={`${driverId}-${m.memberType || "member"}`}
                            className="border rounded-md px-3 py-2 text-xs bg-white"
                          >
                            <div className="flex flex-wrap justify-between gap-2">
                              <div className="font-medium flex items-center gap-2 flex-wrap">
                                <span>{fullName || "—"}</span>

                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    m.memberType === "primary"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-purple-100 text-purple-700"
                                  }`}
                                >
                                  {m.memberType === "primary"
                                    ? "Primary"
                                    : "Secondary"}
                                </span>

                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                    isActiveInTeam
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-600"
                                  }`}
                                >
                                  {isActiveInTeam
                                    ? "Active Team Driver"
                                    : "Inactive"}
                                </span>
                              </div>

                              <span className="inline-flex items-center gap-1">
                                <BadgeCheck size={14} className="opacity-70" />

                                {m.driverStatus || m.status || "—"}
                              </span>
                            </div>

                            <div className="mt-1 grid grid-cols-1 md:grid-cols-4 gap-2 text-gray-600">
                              <div className="flex items-center gap-1">
                                <BadgeCheck size={14} className="opacity-60" />

                                <span className="truncate">
                                  {m.driver_code || "—"}
                                </span>
                              </div>

                              <div className="flex items-center gap-1">
                                <Phone size={14} className="opacity-60" />

                                <span className="truncate">
                                  {m.phone || "—"}
                                </span>
                              </div>

                              <div className="hidden md:flex items-center gap-1">
                                <Mail size={14} className="opacity-60" />

                                <span className="truncate">
                                  {m.email || "—"}
                                </span>
                              </div>

                              <div className="flex items-center justify-end gap-3">
                                <label
                                  className={`relative inline-flex items-center ${
                                    working
                                      ? "opacity-60 pointer-events-none"
                                      : "cursor-pointer"
                                  }`}
                                  title="Toggle duty status"
                                >
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={isOn}
                                    onChange={() =>
                                      !working && onToggleDuty(driverId)
                                    }
                                    disabled={working}
                                    aria-checked={isOn}
                                    aria-label="Toggle duty status"
                                  />

                                  <div
                                    className={[
                                      "w-10 h-5 rounded-full transition-colors duration-300",
                                      "bg-gray-300 peer-checked:bg-green-500",
                                      "ring-0 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-400",
                                    ].join(" ")}
                                  />

                                  <span
                                    className={[
                                      "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow",
                                      "transition-transform duration-300",
                                      "peer-checked:translate-x-[1.25rem]",
                                    ].join(" ")}
                                  />
                                </label>

                                <button
                                  onClick={() => onOpenSingleNotif(m)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Send notification"
                                >
                                  <Bell size={16} />
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>

              {/* Shops */}
              <section className="border rounded-lg bg-white">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Store size={16} />

                    <h3 className="text-sm font-semibold">Assigned Shops</h3>
                  </div>

                  <button
                    onClick={onOpenAssign}
                    className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded"
                  >
                    <Building2 size={14} />
                    Assign Shops
                  </button>
                </div>

                <div className="p-3">
                  {shops.length === 0 ? (
                    <p className="text-xs text-gray-500">No shops assigned.</p>
                  ) : (
                    <ul className="space-y-2">
                      {shops.map((s) => (
                        <li
                          key={s.id}
                          className="border rounded-md px-3 py-2 text-xs bg-white"
                        >
                          <div className="flex flex-wrap justify-between gap-2">
                            <div className="font-medium">{s.shop_name}</div>

                            <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {s.shop_code}
                            </span>
                          </div>

                          <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-600">
                            <div className="flex items-center gap-1 md:col-span-2">
                              <MapPin size={14} className="opacity-60" />

                              <span className="truncate">{s.shop_address}</span>
                            </div>

                            <div className="flex items-center gap-1">
                              <Phone size={14} className="opacity-60" />

                              <span>{s.shop_phone || "—"}</span>
                            </div>
                          </div>

                          <div className="mt-1 text-[11px] text-gray-500">
                            Onboarded: {s.onboardedOn || "—"}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default FragmentRow;

// =====================================================
// HELPERS
// =====================================================

function InfoBadgeInline({ children, color = "gray" }) {
  const styles = {
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700",
    orange: "bg-orange-100 text-orange-700",
    purple: "bg-purple-100 text-purple-700",
    red: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${styles[color]}`}
    >
      {children}
    </span>
  );
}

function formatTeamDate(value) {
  if (!value) return "—";

  try {
    const date = new Date(value);

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function StatusPillInline({ status }) {
  const s = (status || "").toLowerCase();

  const classes =
    s === "publish"
      ? "bg-green-100 text-green-700"
      : s === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-gray-100 text-gray-600";

  const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${classes}`}>
      {label}
    </span>
  );
}
